/**
 * Script front du block ITU YouTube Playlist.
 * Dispatch selon le mode (playlist / carousel / grid). Le mode "single" n'a
 * besoin d'aucun JS. Multi-instance compatible.
 */
document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.youtube-playlist' ).forEach( ( block ) => {
		if ( block.querySelector( '.br-carousel' ) ) {
			initCarousel( block );
		} else if ( block.querySelector( '.br-video-grid' ) ) {
			initGrid( block );
		} else if ( block.querySelector( '.video-list' ) ) {
			initPlaylist( block );
		}
	} );
} );

/* ====================================================================== */
/* MODE PLAYLIST                                                          */
/* ====================================================================== */
function initPlaylist( block ) {
	const iframe = block.querySelector( '.youtube-player-iframe' );
	const items = Array.from( block.querySelectorAll( '.video-item' ) );
	if ( ! iframe || items.length === 0 ) {
		return;
	}

	const parsed = parseInt( block.dataset.itemsPerPage, 10 );
	const itemsPerPage =
		Number.isInteger( parsed ) && parsed > 0 ? parsed : 14;
	const totalPages = Math.max( 1, Math.ceil( items.length / itemsPerPage ) );
	let currentPage = 1;

	const pagination = block.querySelector( '.pagination' );
	const prev = block.querySelector( '.pagination .prev' );
	const next = block.querySelector( '.pagination .next' );
	const status = block.querySelector( '.pagination-status' );

	function showPage( page ) {
		currentPage = Math.min( Math.max( 1, page ), totalPages );
		const start = ( currentPage - 1 ) * itemsPerPage;
		const end = start + itemsPerPage;
		items.forEach( ( item, index ) => {
			item.classList.toggle(
				'is-hidden',
				! ( index >= start && index < end )
			);
		} );
		updatePagination();
	}

	function updatePagination() {
		if ( pagination ) {
			pagination.classList.toggle( 'is-hidden', totalPages <= 1 );
		}
		if ( prev ) {
			prev.disabled = currentPage === 1;
		}
		if ( next ) {
			next.disabled = currentPage === totalPages;
		}
		if ( status ) {
			status.textContent = `${ currentPage } / ${ totalPages }`;
		}
	}

	function playVideo( item ) {
		const url = item.getAttribute( 'data-url' );
		if ( ! url ) {
			return;
		}
		iframe.src = url;
		items.forEach( ( el ) => el.classList.remove( 'is-active' ) );
		item.classList.add( 'is-active' );
	}

	items.forEach( ( item ) => {
		item.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			playVideo( item );
		} );
		item.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' ) {
				e.preventDefault();
				playVideo( item );
			}
		} );
	} );

	if ( prev ) {
		prev.addEventListener( 'click', () => showPage( currentPage - 1 ) );
	}
	if ( next ) {
		next.addEventListener( 'click', () => showPage( currentPage + 1 ) );
	}

	showPage( 1 );
}

/* ====================================================================== */
/* LIGHTBOX (shared by carousel and grid)                                */
/* ====================================================================== */
function initLightbox( block ) {
	const lightbox = block.querySelector( '.br-lightbox' );
	if ( ! lightbox ) return null;

	const lbIframe = lightbox.querySelector( '.br-lightbox-iframe' );
	const lbClose = lightbox.querySelector( '.br-lightbox-close' );
	const lbBackdrop = lightbox.querySelector( '.br-lightbox-backdrop' );
	let lastFocused = null;

	// Move lightbox to <body> so position:fixed is never clipped by a
	// transformed/overflowed ancestor.
	document.body.appendChild( lightbox );

	function open( url ) {
		if ( ! lbIframe || ! url ) return;
		lbIframe.src = url;
		lightbox.hidden = false;
		document.body.classList.add( 'br-lightbox-open' );
		lastFocused = document.activeElement;
		if ( lbClose ) lbClose.focus();
	}

	function close() {
		if ( lightbox.hidden ) return;
		lightbox.hidden = true;
		if ( lbIframe ) lbIframe.src = '';
		document.body.classList.remove( 'br-lightbox-open' );
		if ( lastFocused && lastFocused.focus ) lastFocused.focus();
	}

	if ( lbClose ) lbClose.addEventListener( 'click', close );
	if ( lbBackdrop ) lbBackdrop.addEventListener( 'click', close );
	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' ) close();
	} );

	return { open, close };
}

/* ====================================================================== */
/* MODE CARROUSEL                                                         */
/* ====================================================================== */
function initCarousel( block ) {
	const track = block.querySelector( '.carousel-track' );
	const cards = Array.from( block.querySelectorAll( '.carousel-card' ) );
	if ( ! track || cards.length === 0 ) {
		return;
	}

	const nav = block.querySelector( '.carousel-nav' );
	const prev = block.querySelector( '.carousel-prev' );
	const next = block.querySelector( '.carousel-next' );

	const lb = initLightbox( block );

	/* --- scroll nav --- */
	function step() {
		const card = cards[ 0 ];
		const gap = parseFloat( getComputedStyle( track ).columnGap ) || 24;
		return ( card ? card.offsetWidth : track.clientWidth ) + gap;
	}

	function updateNav() {
		const overflow = track.scrollWidth > track.clientWidth + 2;
		if ( nav ) {
			nav.classList.toggle( 'is-hidden', ! overflow );
		}
		const maxScroll = track.scrollWidth - track.clientWidth - 2;
		if ( prev ) {
			prev.disabled = track.scrollLeft <= 0;
		}
		if ( next ) {
			next.disabled = track.scrollLeft >= maxScroll;
		}
	}

	if ( prev ) {
		prev.addEventListener( 'click', () =>
			track.scrollBy( { left: -step(), behavior: 'smooth' } )
		);
	}
	if ( next ) {
		next.addEventListener( 'click', () =>
			track.scrollBy( { left: step(), behavior: 'smooth' } )
		);
	}
	track.addEventListener( 'scroll', updateNav, { passive: true } );
	window.addEventListener( 'resize', updateNav );
	updateNav();

	/* --- lightbox on card click --- */
	if ( lb ) {
		cards.forEach( ( card ) => {
			card.addEventListener( 'click', () =>
				lb.open( card.getAttribute( 'data-url' ) )
			);
		} );
	}
}

/* ====================================================================== */
/* MODE GRILLE                                                            */
/* ====================================================================== */
function initGrid( block ) {
	const cards = Array.from( block.querySelectorAll( '.grid-card' ) );
	if ( cards.length === 0 ) return;

	const lb = initLightbox( block );
	if ( ! lb ) return;

	cards.forEach( ( card ) => {
		card.addEventListener( 'click', () =>
			lb.open( card.getAttribute( 'data-url' ) )
		);
	} );
}
